import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import gsap from 'gsap';

let camera, scene, renderer;
let droneModel = null;
let scrollY = 0;
let scrollInfluence = 0;   // 0 = home pose, 1 = fully scroll-driven
let assemblyTl = null;
let blendTween = null;     // tracks the scroll-influence blend
const loader = new GLTFLoader();

// Home (assembled) model transforms
function getHomeX() { return window.innerWidth <= 640 ? 0 : 0.35; }
let HOME_X = getHomeX();
const HOME_Y = 0.3;
const HOME_ROT_Y = Math.PI * 1.75;

export function init() {

	camera = new THREE.PerspectiveCamera( 70, window.innerWidth / window.innerHeight, 0.01, 10 );
	camera.position.z = 5;
    camera.rotation.z = 1;

	scene = new THREE.Scene();

    // Bright lighting so colors show on white background
    const ambient = new THREE.AmbientLight( 0xffffff, 3 );
    scene.add( ambient );

    const dirLight1 = new THREE.DirectionalLight(0xffffff, 3);
    dirLight1.position.set(3, 4, 3);
    scene.add(dirLight1);

    const dirLight2 = new THREE.DirectionalLight(0xffffff, 2);
    dirLight2.position.set(-3, 2, -2);
    scene.add(dirLight2);

    const dirLight3 = new THREE.DirectionalLight(0xffffff, 1.5);
    dirLight3.position.set(0, -2, 3);
    scene.add(dirLight3);

    const hemiLight = new THREE.HemisphereLight(0xffffff, 0xe0e0e0, 2);
    scene.add(hemiLight);

    // Camera intro
    gsap.to(camera.position, {
        z: 1,
        duration: 1,
        ease: "back.out(1.7)"
    });
    gsap.to(camera.rotation, {
        z: 0,
        duration: 1
    });

    // Load the GLB and animate its own parts assembling
    loader.load("/models/drone/drone.glb", (gltf) => {
        const model = gltf.scene;
        model.scale.set(.4, .4, .4);
        model.rotation.x = 1;
        model.rotation.y = HOME_ROT_Y;
        model.position.set(HOME_X, HOME_Y, 0);

        // Collect all meshes as individual "parts"
        const parts = [];
        model.traverse((child) => {
            if (child.isMesh) {
                parts.push(child);
            }
        });

        // Save home transforms, then scatter parts
        const homeData = [];
        parts.forEach((part) => {
            homeData.push({
                mesh: part,
                pos: part.position.clone(),
                rot: part.rotation.clone(),
            });

            // Scatter: offset each part randomly
            const spread = 6;
            part.position.x += (Math.random() - 0.5) * spread;
            part.position.y += (Math.random() - 0.5) * spread;
            part.position.z += (Math.random() - 0.5) * spread;
            part.rotation.x += (Math.random() - 0.5) * Math.PI * 2;
            part.rotation.y += (Math.random() - 0.5) * Math.PI * 2;
            part.rotation.z += (Math.random() - 0.5) * Math.PI * 2;

            // Start transparent
            if (part.material) {
                part.material = part.material.clone();
                part.material.transparent = true;
                part.material.opacity = 0;
            }
        });

        scene.add(model);
        droneModel = model;   // set immediately so animation loop can blend

        // ── Build assembly timeline ──
        const onAssemblyComplete = () => {
            // Restore opaque materials
            parts.forEach((part) => {
                if (part.material) {
                    part.material.transparent = false;
                    part.material.opacity = 1;
                }
            });
            // If blend was already started by early scroll, let it finish.
            // Otherwise snap to full scroll influence.
            if (!blendTween || !blendTween.isActive()) {
                scrollInfluence = 1;
            }
        };

        assemblyTl = gsap.timeline({ onComplete: onAssemblyComplete });

        const assemblyDelay = 0.8; // wait for camera intro
        const totalDuration = 2;

        homeData.forEach((data, i) => {
            const delay = assemblyDelay + (i / homeData.length) * 0.8;

            // Fade in
            if (data.mesh.material) {
                assemblyTl.to(data.mesh.material, {
                    opacity: 1,
                    duration: 0.8,
                }, delay);
            }

            // Move to home
            assemblyTl.to(data.mesh.position, {
                x: data.pos.x,
                y: data.pos.y,
                z: data.pos.z,
                duration: totalDuration,
                ease: "power3.inOut",
            }, delay);

            // Rotate to home
            assemblyTl.to(data.mesh.rotation, {
                x: data.rot.x,
                y: data.rot.y,
                z: data.rot.z,
                duration: totalDuration,
                ease: "power3.inOut",
            }, delay);
        });
    });

	renderer = new THREE.WebGLRenderer( { antialias: true, alpha: false } );
	renderer.setPixelRatio( Math.min(window.devicePixelRatio, 2) );
	renderer.setSize( window.innerWidth, window.innerHeight );
	renderer.setAnimationLoop( animation );
    renderer.setClearColor( 0xf7f7f5, 1 );
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
	document.body.appendChild( renderer.domElement );

    window.addEventListener( 'resize', () => {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setPixelRatio( Math.min(window.devicePixelRatio, 2) );
        renderer.setSize( window.innerWidth, window.innerHeight );
        HOME_X = getHomeX();
    });

    // Track scroll — smoothly blend in scroll influence if user scrolls during assembly
    window.addEventListener('scroll', () => {
        scrollY = window.scrollY;
        // If user scrolls while assembly is still running, start a gentle blend
        // so scroll influence ramps in gradually alongside the assembly
        if (scrollY > 20 && scrollInfluence < 1 && !blendTween) {
            blendTween = gsap.to({ v: scrollInfluence }, {
                v: 1,
                duration: 1.5,
                ease: "power2.out",
                onUpdate() { scrollInfluence = this.targets()[0].v; },
                onComplete() { scrollInfluence = 1; },
            });
        }
    });
}

function animation() {
    if (droneModel) {
        const scrollFactor = scrollY / (document.body.scrollHeight - window.innerHeight || 1);
        const isMobile = window.innerWidth <= 640;

        let targetX, targetY, targetRotY, targetRotZ;

        if (isMobile) {
            // On mobile: move sharply right to clear hero text, then return at the learning section
            const earlyScroll = Math.min(scrollFactor * 8, 1); // 0→1 over first ~12% of page
            // Return phase: ramp 0→1 around the learning section (~25%-45% of page)
            const returnPhase = Math.min(1, Math.max(0, (scrollFactor - 0.25) / 0.2));
            // X: move right early, then come back to center at the learning section
            const awayX = HOME_X + earlyScroll * 0.9;
            targetX = awayX * (1 - returnPhase) + HOME_X * returnPhase + Math.sin(scrollFactor * Math.PI * 2) * 0.06;
            // Y: drift up then down, return to near-home at the learning section
            const awayY = HOME_Y + earlyScroll * 0.45 - scrollFactor * 0.7;
            targetY = awayY * (1 - returnPhase) + (HOME_Y - 0.1) * returnPhase;
            targetRotY = HOME_ROT_Y + scrollFactor * Math.PI * 2;
            targetRotZ = Math.sin(scrollFactor * Math.PI * 3) * 0.1;

            // Hide before the waitlist: shrink from 60%-75% scroll
            const hidePhase = Math.min(1, Math.max(0, (scrollFactor - 0.6) / 0.15));
            const s = 0.4 * (1 - hidePhase);
            droneModel.scale.set(s, s, s);
        } else {
            // Desktop: push drone to top-right corner around the learning section
            // so it flies by without crossing the centered text
            const learnProximity = 1 - Math.min(1, Math.abs(scrollFactor - 0.35) / 0.3); // wider, smoother arc
            targetX = HOME_X + Math.sin(scrollFactor * Math.PI * 2) * 0.15 + learnProximity * 0.5;
            targetY = HOME_Y - scrollFactor * 1.2 + learnProximity * 0.6;
            targetRotY = HOME_ROT_Y + scrollFactor * Math.PI * 2;
            targetRotZ = Math.sin(scrollFactor * Math.PI * 3) * 0.15 + learnProximity * 0.1;
        }

        // Blend between home pose and scroll-driven pose
        const t = scrollInfluence;
        droneModel.position.x = HOME_X + (targetX - HOME_X) * t;
        droneModel.position.y = HOME_Y + (targetY - HOME_Y) * t;
        droneModel.rotation.y = HOME_ROT_Y + (targetRotY - HOME_ROT_Y) * t;
        droneModel.rotation.z = targetRotZ * t;
    }

	renderer.render( scene, camera );
}
